import { AppRoutes } from '@types';
import type { NextRouter } from 'next/router';

const BASE_PATH = '/cal';

const EXCLUDED_ROUTES: readonly string[] = [
  `${AppRoutes.dataCollection}?completed=true`,
  `${BASE_PATH}${AppRoutes.aiModelAdd}/`,
  `${BASE_PATH}${AppRoutes.useCaseDetails}/`,
  `${BASE_PATH}${AppRoutes.dataCollection}/#completed`,
  // `${BASE_PATH}${AppRoutes.governanceEvaluation}`, // show warning when user switch between custom questionnaires
  `${BASE_PATH}${AppRoutes.dataCollection}`,
];

interface HandleRouteChangeParams {
  targetUrl: string;
  completed: boolean;
  showTestingView: boolean;
  showQuestionnaire: boolean;
  pathname: string;
  explicitelyLeavePage: { current: boolean };
  setShowExitModal: (show: boolean) => void;
  setRedirectUrl: (url: string) => void;
  events: NextRouter['events'];
}

const leavePage = (e: BeforeUnloadEvent | Event, condition: boolean): void => {
  if (condition) {
    e.preventDefault();
    if ('returnValue' in e) {
      e.returnValue = '';
    }
  }
};

const routeChange = (
  url: string,
  condition: boolean,
  setShowExitModal: (show: boolean) => void,
  setRedirectUrl: (url: string) => void,
  events: NextRouter['events'],
): void => {
  if (condition) {
    setShowExitModal(true);
    setRedirectUrl(url.replace('/cal', ''));
    events.emit('routeChangeError');
    // eslint-disable-next-line
    throw `Route change to "${url}" was aborted (this error can be safely ignored). See https://github.com/zeit/next.js/issues/2476.`;
  }
};

const handleRouteChange = ({
  targetUrl,
  completed,
  showTestingView,
  showQuestionnaire,
  pathname,
  explicitelyLeavePage,
  setShowExitModal,
  setRedirectUrl,
  events,
}: HandleRouteChangeParams): void => {
  const isActiveSession = !completed && (showTestingView || showQuestionnaire);
  const isNavigatingAway = pathname !== targetUrl;
  const isIntentionalLeave = explicitelyLeavePage.current;
  const isAllowedRoute = EXCLUDED_ROUTES.some(url => targetUrl.startsWith(url));

  const shouldPreventNavigation = isActiveSession && isNavigatingAway && !isIntentionalLeave && !isAllowedRoute;

  routeChange(targetUrl, shouldPreventNavigation, setShowExitModal, setRedirectUrl, events);
};

export const NavigationHelper = {
  handleRouteChange,
  leavePage,
  routeChange,
};
